import { useEffect, useRef } from 'react'
import { Controller, useFormContext } from '@astro/forms'

function Pad({ value, onChange, error }) {
  const canvasRef = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.strokeStyle = '#1C0A04'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'

    // Re-entering this step remounts the canvas — restore the saved strokes.
    if (value) {
      const img = new Image()
      img.onload = () => ctx.drawImage(img, 0, 0)
      img.src = value
    }

    let drawing = false
    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect()
      const point = e.touches ? e.touches[0] : e
      return {
        x: (point.clientX - rect.left) * (canvas.width / rect.width),
        y: (point.clientY - rect.top) * (canvas.height / rect.height),
      }
    }
    const start = (e) => {
      drawing = true
      const p = getPos(e)
      ctx.beginPath()
      ctx.moveTo(p.x, p.y)
      e.preventDefault()
    }
    const move = (e) => {
      if (!drawing) return
      const p = getPos(e)
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
      e.preventDefault()
    }
    const end = () => {
      if (!drawing) return
      drawing = false
      onChangeRef.current(canvas.toDataURL())
    }

    canvas.addEventListener('mousedown', start)
    canvas.addEventListener('mousemove', move)
    window.addEventListener('mouseup', end)
    canvas.addEventListener('touchstart', start, { passive: false })
    canvas.addEventListener('touchmove', move, { passive: false })
    canvas.addEventListener('touchend', end)
    return () => {
      canvas.removeEventListener('mousedown', start)
      canvas.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', end)
      canvas.removeEventListener('touchstart', start)
      canvas.removeEventListener('touchmove', move)
      canvas.removeEventListener('touchend', end)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clear = () => {
    const canvas = canvasRef.current
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
    onChange('')
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="ar-label mb-0">Signature</span>
        <button
          type="button"
          onClick={clear}
          className="text-[10px] font-bold uppercase tracking-wider text-goldAccent hover:underline"
        >
          Clear
        </button>
      </div>
      <canvas
        ref={canvasRef}
        width="600"
        height="150"
        className="ar-sig-pad w-full border border-stone-200 rounded-xl bg-stone-50"
      />
      <p className="text-[11px] text-stone-400 mt-1.5">Draw your signature above using mouse or touch.</p>
      {error && <p className="ar-error">{error.message}</p>}
    </div>
  )
}

export default function SignaturePad({ name }) {
  const { control } = useFormContext()
  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: 'Please draw your signature to continue.' }}
      render={({ field, fieldState }) => (
        <Pad value={field.value} onChange={field.onChange} error={fieldState.error} />
      )}
    />
  )
}
